import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtCurrency } from "../../lib/api";
import MobileHeader from "../../components/MobileHeader";
import { Button } from "../../components/ui/button";
import { Slider } from "../../components/ui/slider";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const META = {
    BizCredit: { color: "#0066FF", title: "BizCredit™", min: 50000, max: 5000000, tenures: [30, 60, 90, 180, 365] },
    TradeCredit: { color: "#06B6D4", title: "TradeCredit™", min: 100000, max: 10000000, tenures: [30, 60, 90] },
    StockFinance: { color: "#FF6B00", title: "StockFinance™", min: 50000, max: 7500000, tenures: [60, 90, 180] },
    AgroCredit: { color: "#00D084", title: "AgroCredit™", min: 20000, max: 2000000, tenures: [90, 180, 365] },
    FleetFinance: { color: "#7000FF", title: "FleetFinance™", min: 100000, max: 5000000, tenures: [180, 365] },
};

export default function BusinessLoanApply() {
    const { product } = useParams();
    const nav = useNavigate();
    const meta = META[product] || META.BizCredit;
    const [amount, setAmount] = useState(meta.min * 5);
    const [tenure, setTenure] = useState(meta.tenures[1] || meta.tenures[0]);
    const [quote, setQuote] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.post("/loans/quote", { product, amount, tenure_days: tenure })
            .then((r) => setQuote(r.data))
            .catch(() => setQuote(null));
    }, [amount, tenure, product]);

    const submit = async () => {
        setBusy(true);
        try {
            const r = await api.post("/loans/apply", { product, amount, tenure_days: tenure });
            toast.success("Loan disbursed!");
            nav(`/business/loans/detail/${r.data.loan.id}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="business-apply-page">
            <MobileHeader title={meta.title} backTo="/business/loans" variant="blue" />
            <div className="px-5 -mt-6 pb-4 space-y-4">
                <div className="bg-white rounded-2xl p-6 border border-slate-200 product-shadow">
                    <div className="text-sm font-semibold text-slate-700">How much does your business need?</div>
                    <div className="text-center mt-6">
                        <div className="font-display text-3xl font-bold" style={{ color: meta.color }}>{fmtCurrency(amount)}</div>
                    </div>
                    <Slider min={meta.min} max={meta.max} step={5000} value={[amount]} onValueChange={(v) => setAmount(v[0])} className="mt-6" data-testid="biz-amount-slider" />
                    <div className="flex justify-between text-xs text-slate-400 mt-3">
                        <span>{fmtCurrency(meta.min, "NGN", { compact: true })}</span>
                        <span>{fmtCurrency(meta.max, "NGN", { compact: true })}</span>
                    </div>
                    <div className="mt-6">
                        <div className="text-sm font-semibold text-slate-700 mb-3">Repayment tenure</div>
                        <div className="grid grid-cols-3 gap-2">
                            {meta.tenures.map((t) => (
                                <button key={t} onClick={() => setTenure(t)} data-testid={`biz-tenure-${t}`} className={`py-3 rounded-xl border-2 text-sm font-semibold tap-feedback transition ${tenure === t ? "text-white" : "border-slate-200 text-slate-600"}`} style={tenure === t ? { background: meta.color, borderColor: meta.color } : {}}>
                                    {t}d
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {quote && (
                    <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow space-y-2">
                        <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Repayment</div>
                        <div className="font-display text-2xl font-bold">{fmtCurrency(quote.total_repayment)}</div>
                        <div className="text-xs text-slate-500">Due in {tenure} days</div>
                        <div className="border-t border-slate-100 pt-3 space-y-1.5 text-sm">
                            <Row label="Principal" value={fmtCurrency(quote.amount)} />
                            <Row label="Interest" value={fmtCurrency(quote.interest)} />
                            <Row label="Fees" value={fmtCurrency(quote.fees)} />
                        </div>
                    </div>
                )}

                <Button onClick={submit} disabled={busy} className="w-full h-12 rounded-xl font-semibold" style={{ background: meta.color }} data-testid="biz-submit-btn">
                    {busy ? "Approving…" : "Apply & Receive Funds"}
                </Button>
                <div className="text-center text-xs text-slate-500 flex justify-center gap-1 items-center">
                    <CheckCircle2 size={12} className="text-emerald-500" /> Funds disbursed instantly to your business wallet
                </div>
            </div>
        </div>
    );
}

function Row({ label, value }) {
    return <div className="flex justify-between"><span className="text-slate-500">{label}</span><span className="font-semibold">{value}</span></div>;
}
